import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ApexCharts 3.54 (the version locked in package.json) throws "Cannot read
// properties of undefined (reading 'length')" from inside its async create()
// promise on edge-case data shapes. Those throws bypass React ErrorBoundary
// (boundaries don't catch promise rejections) and surface as a webpack-dev
// red overlay that nukes the entire dashboard, even though the chart slot
// is the only thing actually broken.
//
// Swallow that specific noise so the rest of the page stays usable. Any
// other runtime error continues to surface normally.
if (typeof window !== "undefined") {
  const looksLikeApexNoise = (err) => {
    const msg = String(err?.message || err || "");
    const stack = String(err?.stack || "");
    if (!/reading 'length'/.test(msg)) return false;
    // Either the source-mapped or the minified production stack.
    return /apexcharts/i.test(stack) || /at new Promise/.test(stack);
  };

  window.addEventListener("error", (event) => {
    if (looksLikeApexNoise(event.error)) {
      // eslint-disable-next-line no-console
      console.warn("[ChartGuard] swallowed:", event.error?.message);
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
    return undefined;
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (looksLikeApexNoise(event.reason)) {
      // eslint-disable-next-line no-console
      console.warn("[ChartGuard] swallowed:", event.reason?.message);
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
    return undefined;
  });

  // webpack-dev-server / CRA dev overlay is rendered into the DOM via a
  // separate listener we cannot unregister. preventDefault on our handler
  // above does NOT stop it. So we additionally tear the overlay element
  // out of the DOM whenever it appears within 2s of a swallowed error.
  //
  // Production has no overlay at all — this entire block is a no-op there
  // (gated on NODE_ENV). End users on prod never see this even if the
  // underlying error keeps firing.
  if (process.env.NODE_ENV !== "production") {
    let lastSwallowedAt = 0;
    const markSwallowed = () => { lastSwallowedAt = Date.now(); };
    window.addEventListener("error", (e) => {
      if (looksLikeApexNoise(e.error)) markSwallowed();
    }, true);
    window.addEventListener("unhandledrejection", (e) => {
      if (looksLikeApexNoise(e.reason)) markSwallowed();
    }, true);

    const OVERLAY_TEXT_SIGNATURE = /Uncaught runtime errors/i;

    const looksLikeOverlay = (node) => {
      if (!node || node.nodeType !== 1) return false;
      const id = String(node.id || "");
      const tag = String(node.tagName || "").toLowerCase();
      // Known explicit ids
      if (id.includes("webpack-dev-server-client-overlay")) return true;
      if (tag === "react-error-overlay") return true;
      // Iframes added at the top level of body are nearly always the overlay
      if (tag === "iframe" && node.parentElement === document.body) return true;
      // Text-signature catch-all — covers whatever element / shadow root CRA
      // is using right now
      try {
        const txt = (node.innerText || node.textContent || "").slice(0, 500);
        if (OVERLAY_TEXT_SIGNATURE.test(txt)) return true;
      } catch {
        // shadow-root or cross-origin iframe — fall through
      }
      return false;
    };

    const dismissOverlayIfStale = (mutations) => {
      if (Date.now() - lastSwallowedAt > 2000) return;
      for (const m of mutations || []) {
        m.addedNodes && m.addedNodes.forEach((n) => {
          if (looksLikeOverlay(n)) {
            try { n.remove(); } catch {}
          }
        });
      }
      // Belt + braces — also sweep the document for any matching element
      try {
        document.querySelectorAll("body > iframe, [id*='overlay']").forEach((n) => {
          if (looksLikeOverlay(n)) {
            try { n.remove(); } catch {}
          }
        });
      } catch {}
    };

    const observer = new MutationObserver(dismissOverlayIfStale);
    const start = () => {
      if (document.documentElement) {
        observer.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
      } else {
        requestAnimationFrame(start);
      }
    };
    start();
  }
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <BrowserRouter>
    <>
      <App />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  </BrowserRouter>
);
